"use client"
import { useEffect } from 'react'

interface VideoModalProps {
  isOpen: boolean
  onClose: () => void
  youtubeId: string
  title: string
}

export default function VideoModal({ isOpen, onClose, youtubeId, title }: VideoModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - Responsive: plein écran sur mobile, centré sur desktop */}
      <div className="relative z-10 w-full h-full md:h-auto md:max-w-4xl md:mx-4">
        {/* Header - Fixe en haut sur mobile */}
        <div className="flex items-center justify-between p-4 md:mb-4 bg-black/50 md:bg-transparent">
          <h3 className="text-base md:text-lg font-semibold text-white line-clamp-2 flex-1 mr-4">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="flex h-10 w-10 md:h-8 md:w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Fermer la vidéo"
          >
            <svg className="h-6 w-6 md:h-5 md:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Video Container - Plein écran sur mobile */}
        <div className="relative w-full h-[calc(100vh-120px)] md:h-auto md:aspect-video overflow-hidden md:rounded-lg bg-black shadow-2xl">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
        
        {/* Footer - Fixe en bas sur mobile */}
        <div className="flex items-center justify-between p-4 text-sm text-white/70 bg-black/50 md:bg-transparent md:mt-4">
          <span className="hidden md:block">Appuyez sur Échap pour fermer</span>
          <a
            href={`https://www.youtube.com/watch?v=${youtubeId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-red-400 hover:text-red-300 mx-auto md:mx-0 rounded px-2 py-1 transition-colors hover:bg-white/10"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <span className="md:hidden">Ouvrir dans YouTube</span>
            <span className="hidden md:block">Voir sur YouTube</span>
          </a>
        </div>
      </div>
    </div>
  )

  return modalContent
}